import { CouldNotDecryptError } from '../errors';
import { SALT_SYMMETRIC } from '../salts';
import { Brand } from '../types';
import {
  ArrayOrBase64,
  deriveSecretFromSeedModern,
  loadArrayOrBase64,
  randomBytes,
  toArrayBufferBacked,
} from '../utils';

export type RawSymAesEncryptionKey = Brand<Uint8Array<ArrayBuffer>, 'RawSymAesEncryptionKey'>;
export type SymAesEncryptionKey = Brand<CryptoKey, 'SymAesEncryptionKey'>;
export type SymAesEncryptedBlob = Brand<Uint8Array<ArrayBuffer>, 'SymAesEncryptedBlob'>;

const KEY_LENGTH = 32; // AES-256
const IV_LENGTH = 12; // 96-bit IV, recommended for AES-GCM

const KEY_USAGES: KeyUsage[] = ['encrypt', 'decrypt'];

export function loadSymAesEncryptedBlob(data: ArrayOrBase64): SymAesEncryptedBlob {
  return loadArrayOrBase64(data) as SymAesEncryptedBlob;
}

export function loadRawSymAesEncryptionKey(data: ArrayOrBase64): RawSymAesEncryptionKey {
  return loadArrayOrBase64(data, KEY_LENGTH) as RawSymAesEncryptionKey;
}

// Wrap an existing AES-GCM CryptoKey (e.g. one derived, unwrapped, or imported elsewhere) into
// the branded type. The caller is responsible for ensuring it is an AES-GCM key usable for
// encrypt/decrypt.
export function wrapSymAesEncryptionKey(cryptoKey: CryptoKey): SymAesEncryptionKey {
  if (cryptoKey.algorithm.name !== 'AES-GCM') {
    throw new Error('tried to wrap cryptokey that was not AES-GCM with wrapSymAesEncryptionKey');
  }
  return cryptoKey as SymAesEncryptionKey;
}

export async function loadSymAesEncryptionKey(data: ArrayOrBase64): Promise<SymAesEncryptionKey> {
  const raw = loadRawSymAesEncryptionKey(data);
  return wrapSymAesEncryptionKey(
    await crypto.subtle.importKey('raw', raw, 'AES-GCM', true, KEY_USAGES),
  );
}

export async function serializeSymAesEncryptionKey(
  key: SymAesEncryptionKey,
): Promise<RawSymAesEncryptionKey> {
  return loadRawSymAesEncryptionKey(new Uint8Array(await crypto.subtle.exportKey('raw', key)));
}

// Unlike `deriveSymEncryptionKey` (which chains plain SHA-512 hashes via deriveSecretFromSeed),
// this uses the HMAC-SHA-256 based derivation. It produces different bytes for the same inputs,
// so only use it for newly derived keys.
export async function deriveSymAesEncryptionKey(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
): Promise<SymAesEncryptionKey> {
  return loadSymAesEncryptionKey(
    // We use the -modern variant here, since we have no deployed code that
    // depends on the legacy key derivation for aes symmetric encryption.
    await deriveSecretFromSeedModern(seed, secondarySeed, SALT_SYMMETRIC + salt, KEY_LENGTH),
  );
}

export async function newSymAesEncryptionKey(): Promise<SymAesEncryptionKey> {
  return wrapSymAesEncryptionKey(
    await crypto.subtle.generateKey({ name: 'AES-GCM', length: KEY_LENGTH * 8 }, true, KEY_USAGES),
  );
}

// `additionalData` is authenticated but not encrypted, and is not stored in the blob. The exact
// same value must be supplied to `symDecryptAes`, otherwise decryption fails.
export async function symAesEncrypt(
  key: SymAesEncryptionKey,
  data: Uint8Array,
  additionalData?: Uint8Array,
): Promise<SymAesEncryptedBlob> {
  const iv = randomBytes(IV_LENGTH);
  const params: AesGcmParams = { name: 'AES-GCM', iv };
  if (additionalData !== undefined) {
    params.additionalData = toArrayBufferBacked(additionalData);
  }
  const box = new Uint8Array(await crypto.subtle.encrypt(params, key, toArrayBufferBacked(data)));

  const fullMessage = new Uint8Array(iv.length + box.length);
  fullMessage.set(iv);
  fullMessage.set(box, iv.length);

  return loadSymAesEncryptedBlob(fullMessage);
}

export async function symAesDecrypt(
  key: SymAesEncryptionKey,
  data: SymAesEncryptedBlob,
  additionalData?: Uint8Array,
): Promise<Uint8Array<ArrayBuffer>> {
  const iv = data.slice(0, IV_LENGTH);
  const box = data.slice(IV_LENGTH);
  const params: AesGcmParams = { name: 'AES-GCM', iv };
  if (additionalData !== undefined) {
    params.additionalData = toArrayBufferBacked(additionalData);
  }

  try {
    return new Uint8Array(await crypto.subtle.decrypt(params, key, box));
  } catch {
    throw new CouldNotDecryptError();
  }
}
